'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type LicenseType = 'MIT' | 'Apache-2.0' | 'BSD-3' | 'GPL-2.0' | 'GPL-3.0' | 'LGPL' | 'MPL' | 'AGPL' | 'ISC' | 'Unlicense' | 'Proprietary' | 'Unknown';
type CommercialUse = 'allowed' | 'restricted' | 'forbidden';
type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

interface Package {
  id: string;
  name: string;
  version: string;
  license: LicenseType;
}

interface LicenseInfo {
  name: string;
  commercialUse: CommercialUse;
  requiresAttribution: boolean;
  requiresSourceDisclosure: boolean;
  copyleft: boolean;
  riskLevel: RiskLevel;
  description: string;
  obligations: string[];
}

const LICENSE_DATABASE: Record<LicenseType, LicenseInfo> = {
  'MIT': {
    name: 'MIT License',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: false,
    copyleft: false,
    riskLevel: 'low',
    description: 'Permissive license with minimal restrictions',
    obligations: ['Include copyright notice', 'Include license text'],
  },
  'Apache-2.0': {
    name: 'Apache License 2.0',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: false,
    copyleft: false,
    riskLevel: 'low',
    description: 'Permissive license with patent grant',
    obligations: ['Include copyright notice', 'Include license text', 'State significant changes', 'Include NOTICE file if present'],
  },
  'BSD-3': {
    name: 'BSD 3-Clause License',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: false,
    copyleft: false,
    riskLevel: 'low',
    description: 'Permissive license similar to MIT',
    obligations: ['Include copyright notice', 'Include license text', 'No endorsement using author names'],
  },
  'GPL-2.0': {
    name: 'GNU GPL v2',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: true,
    copyleft: true,
    riskLevel: 'critical',
    description: 'Strong copyleft - derivative works must be GPL',
    obligations: ['Disclose source code', 'Include license text', 'State changes', 'Same license for derivatives'],
  },
  'GPL-3.0': {
    name: 'GNU GPL v3',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: true,
    copyleft: true,
    riskLevel: 'critical',
    description: 'Strong copyleft with patent and DRM provisions',
    obligations: ['Disclose source code', 'Include license text', 'State changes', 'Same license for derivatives', 'Provide installation information'],
  },
  'LGPL': {
    name: 'GNU LGPL',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: true,
    copyleft: true,
    riskLevel: 'high',
    description: 'Weak copyleft - only library modifications must be LGPL',
    obligations: ['Disclose library source code', 'Include license text', 'Allow library replacement'],
  },
  'AGPL': {
    name: 'GNU AGPL',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: true,
    copyleft: true,
    riskLevel: 'critical',
    description: 'Network copyleft - triggers on network use',
    obligations: ['Disclose source code', 'Include license text', 'Same license for derivatives', 'Provide source to network users'],
  },
  'MPL': {
    name: 'Mozilla Public License',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: true,
    copyleft: true,
    riskLevel: 'medium',
    description: 'File-level copyleft',
    obligations: ['Disclose modified files', 'Include license text', 'Same license for modified files'],
  },
  'ISC': {
    name: 'ISC License',
    commercialUse: 'allowed',
    requiresAttribution: true,
    requiresSourceDisclosure: false,
    copyleft: false,
    riskLevel: 'low',
    description: 'Functionally equivalent to MIT',
    obligations: ['Include copyright notice', 'Include license text'],
  },
  'Unlicense': {
    name: 'Unlicense',
    commercialUse: 'allowed',
    requiresAttribution: false,
    requiresSourceDisclosure: false,
    copyleft: false,
    riskLevel: 'low',
    description: 'Public domain dedication',
    obligations: [],
  },
  'Proprietary': {
    name: 'Proprietary License',
    commercialUse: 'restricted',
    requiresAttribution: true,
    requiresSourceDisclosure: false,
    copyleft: false,
    riskLevel: 'high',
    description: 'Check specific license terms',
    obligations: ['Review license agreement', 'May require commercial license'],
  },
  'Unknown': {
    name: 'Unknown License',
    commercialUse: 'forbidden',
    requiresAttribution: true,
    requiresSourceDisclosure: false,
    copyleft: false,
    riskLevel: 'critical',
    description: 'Cannot determine license - high risk',
    obligations: ['Contact package maintainer', 'Review source code', 'Assume restrictive terms'],
  },
};

export default function LicenseComplianceCheckerPage() {
  // Packages (free tier limited to 20)
  const [packages, setPackages] = useState<Package[]>([
    { id: '1', name: 'react', version: '18.2.0', license: 'MIT' },
    { id: '2', name: 'express', version: '4.18.2', license: 'MIT' },
  ]);

  // Commercial use context
  const [isCommercialProduct, setIsCommercialProduct] = useState(true);
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'license-compliance-checker',
    toolCategory: 'compliance',
  });

  // Analysis
  const analysis = useMemo(() => {
    const validPackages = packages.filter(p => p.name.trim());

    // License distribution
    const licenseCounts = validPackages.reduce((acc, pkg) => {
      acc[pkg.license] = (acc[pkg.license] || 0) + 1;
      return acc;
    }, {} as Record<LicenseType, number>);

    // Risk assessment
    const riskCounts = validPackages.reduce((acc, pkg) => {
      const riskLevel = LICENSE_DATABASE[pkg.license].riskLevel;
      acc[riskLevel] = (acc[riskLevel] || 0) + 1;
      return acc;
    }, {} as Record<RiskLevel, number>);

    // GPL/AGPL conflicts
    const gplPackages = validPackages.filter(p =>
      p.license === 'GPL-2.0' || p.license === 'GPL-3.0' || p.license === 'AGPL'
    );

    const lgplPackages = validPackages.filter(p => p.license === 'LGPL');

    // Commercial use conflicts
    const commercialIssues = validPackages.filter(p => {
      if (!isCommercialProduct) return false;
      const licenseInfo = LICENSE_DATABASE[p.license];
      return licenseInfo.commercialUse === 'forbidden' || licenseInfo.commercialUse === 'restricted';
    });

    // Attribution requirements
    const requiresAttribution = validPackages.filter(p =>
      LICENSE_DATABASE[p.license].requiresAttribution
    );

    // Source disclosure requirements
    const requiresSourceDisclosure = validPackages.filter(p =>
      LICENSE_DATABASE[p.license].requiresSourceDisclosure
    );

    // Copyleft packages
    const copyleftPackages = validPackages.filter(p =>
      LICENSE_DATABASE[p.license].copyleft
    );

    // Overall compliance status
    const hasCriticalIssues = gplPackages.length > 0 || commercialIssues.length > 0;
    const hasHighRiskIssues = (riskCounts.high || 0) > 0 || lgplPackages.length > 0;

    const complianceStatus = hasCriticalIssues ? 'critical' :
                            hasHighRiskIssues ? 'warning' : 'compliant';

    return {
      totalPackages: validPackages.length,
      licenseCounts,
      riskCounts,
      gplPackages,
      lgplPackages,
      commercialIssues,
      requiresAttribution,
      requiresSourceDisclosure,
      copyleftPackages,
      complianceStatus,
      hasCriticalIssues,
      hasHighRiskIssues,
      validPackages,
    };
  }, [packages, isCommercialProduct]);

  // Package functions
  const addPackage = () => {
    if (packages.length >= 20) {
      alert('Free tier limited to 20 packages. Upgrade to Pro for unlimited scanning.');
      return;
    }
    setPackages([
      ...packages,
      { id: Date.now().toString(), name: '', version: '1.0.0', license: 'MIT' },
    ]);
  };

  const updatePackage = (id: string, field: keyof Package, value: string | LicenseType) => {
    setPackages(packages.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const removePackage = (id: string) => {
    setPackages(packages.filter(p => p.id !== id));
  };

  // Export to Excel
  const exportToExcel = () => {
    if (typeof window === 'undefined') return;

    const script = document.createElement('script');
    script.src = 'https://cdn.sheetjs.com/xlsx-0.20.3/package/dist/xlsx.full.min.js';
    script.onload = () => {
      const XLSX = (window as any).XLSX;
      const wb = XLSX.utils.book_new();

      // Sheet 1: Compliance Summary
      const summaryData = [
        ['Open Source License Compliance Report', ''],
        ['', ''],
        ['Overview', ''],
        ['Total Packages', analysis.totalPackages],
        ['Compliance Status', analysis.complianceStatus.toUpperCase()],
        ['Commercial Product', isCommercialProduct ? 'Yes' : 'No'],
        ['', ''],
        ['Risk Assessment', ''],
        ['Critical Risk Packages', analysis.riskCounts.critical || 0],
        ['High Risk Packages', analysis.riskCounts.high || 0],
        ['Medium Risk Packages', analysis.riskCounts.medium || 0],
        ['Low Risk Packages', analysis.riskCounts.low || 0],
        ['', ''],
        ['Issues', ''],
        ['GPL/AGPL Conflicts', analysis.gplPackages.length],
        ['Commercial Use Issues', analysis.commercialIssues.length],
        ['Copyleft Packages', analysis.copyleftPackages.length],
        ['', ''],
        ['Obligations', ''],
        ['Requires Attribution', analysis.requiresAttribution.length],
        ['Requires Source Disclosure', analysis.requiresSourceDisclosure.length],
      ];

      const ws1 = XLSX.utils.aoa_to_sheet(summaryData);
      ws1['!cols'] = [{ wch: 30 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Sheet 2: Package List
      const packageData = analysis.validPackages.map(pkg => {
        const licenseInfo = LICENSE_DATABASE[pkg.license];
        return {
          'Package': pkg.name,
          'Version': pkg.version,
          'License': licenseInfo.name,
          'Risk Level': licenseInfo.riskLevel.toUpperCase(),
          'Commercial Use': licenseInfo.commercialUse.toUpperCase(),
          'Attribution Required': licenseInfo.requiresAttribution ? 'Yes' : 'No',
          'Source Disclosure': licenseInfo.requiresSourceDisclosure ? 'Yes' : 'No',
          'Copyleft': licenseInfo.copyleft ? 'Yes' : 'No',
        };
      });

      const ws2 = XLSX.utils.json_to_sheet(packageData);
      ws2['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 25 }, { wch: 12 }, { wch: 18 }, { wch: 20 }, { wch: 18 }, { wch: 12 }];
      XLSX.utils.book_append_sheet(wb, ws2, 'Packages');

      // Sheet 3: Obligations
      const obligationsData: any[] = [];
      const uniqueObligations = new Set<string>();

      analysis.validPackages.forEach(pkg => {
        const licenseInfo = LICENSE_DATABASE[pkg.license];
        licenseInfo.obligations.forEach(obligation => {
          if (!uniqueObligations.has(obligation)) {
            uniqueObligations.add(obligation);
            obligationsData.push({
              'Obligation': obligation,
              'Example Package': pkg.name,
              'License Type': licenseInfo.name,
            });
          }
        });
      });

      if (obligationsData.length > 0) {
        const ws3 = XLSX.utils.json_to_sheet(obligationsData);
        ws3['!cols'] = [{ wch: 40 }, { wch: 25 }, { wch: 25 }];
        XLSX.utils.book_append_sheet(wb, ws3, 'Obligations');
      }

      // Sheet 4: High Risk Packages
      const highRiskPackages = analysis.validPackages
        .filter(pkg => {
          const risk = LICENSE_DATABASE[pkg.license].riskLevel;
          return risk === 'critical' || risk === 'high';
        })
        .map(pkg => {
          const licenseInfo = LICENSE_DATABASE[pkg.license];
          return {
            'Package': pkg.name,
            'Version': pkg.version,
            'License': licenseInfo.name,
            'Risk Level': licenseInfo.riskLevel.toUpperCase(),
            'Issue': licenseInfo.description,
            'Recommendation': licenseInfo.copyleft ? 'Consider alternative or isolate in separate service' : 'Review license terms carefully',
          };
        });

      if (highRiskPackages.length > 0) {
        const ws4 = XLSX.utils.json_to_sheet(highRiskPackages);
        ws4['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 40 }, { wch: 50 }];
        XLSX.utils.book_append_sheet(wb, ws4, 'High Risk Packages');
      }

      (wb.Workbook as any) = { CalcPr: { fullCalcOnLoad: true } };
      XLSX.writeFile(wb, 'license-compliance-report.xlsx');
    };
    document.head.appendChild(script);
  };

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!commandCenterEnabled) return;

    // Calculate overall score based on compliance status
    const overallScore = analysis.complianceStatus === 'compliant' ? 100 :
                         analysis.complianceStatus === 'warning' ? 60 : 20;

    // Format gaps from high-risk packages
    const gaps = [
      ...analysis.gplPackages.map(pkg => ({
        id: pkg.id,
        title: `GPL/AGPL Package: ${pkg.name}`,
        severity: 'critical' as const,
        description: `${pkg.name} (${pkg.version}) uses ${LICENSE_DATABASE[pkg.license].name} which requires source disclosure`,
        recommendation: 'Consider replacing with a permissively licensed alternative or isolate in a separate service',
      })),
      ...analysis.commercialIssues.map(pkg => ({
        id: pkg.id,
        title: `Commercial Use Issue: ${pkg.name}`,
        severity: 'critical' as const,
        description: `${pkg.name} has commercial use restrictions (${LICENSE_DATABASE[pkg.license].commercialUse})`,
        recommendation: 'Review license terms or obtain commercial license if required',
      })),
      ...analysis.validPackages
        .filter(pkg => LICENSE_DATABASE[pkg.license].riskLevel === 'high')
        .filter(pkg => !analysis.gplPackages.includes(pkg) && !analysis.commercialIssues.includes(pkg))
        .map(pkg => ({
          id: pkg.id,
          title: `High Risk License: ${pkg.name}`,
          severity: 'high' as const,
          description: `${pkg.name} uses ${LICENSE_DATABASE[pkg.license].name}: ${LICENSE_DATABASE[pkg.license].description}`,
          recommendation: LICENSE_DATABASE[pkg.license].obligations.join('; '),
        })),
    ];

    // Format recommendations
    const recommendations = [];
    if (analysis.gplPackages.length > 0) {
      recommendations.push({
        id: 'gpl-remediation',
        title: 'Address GPL/AGPL Dependencies',
        priority: 'high' as const,
        description: `${analysis.gplPackages.length} package(s) require source code disclosure. Consider alternatives or architectural isolation.`,
        effort: 'High',
      });
    }
    if (analysis.commercialIssues.length > 0) {
      recommendations.push({
        id: 'commercial-remediation',
        title: 'Resolve Commercial Use Conflicts',
        priority: 'high' as const,
        description: `${analysis.commercialIssues.length} package(s) have commercial use restrictions. Review and obtain licenses if needed.`,
        effort: 'Medium',
      });
    }
    if (analysis.requiresAttribution.length > 0) {
      recommendations.push({
        id: 'attribution-compliance',
        title: 'Implement License Attribution',
        priority: 'medium' as const,
        description: `${analysis.requiresAttribution.length} package(s) require attribution. Create LICENSES.txt file.`,
        effort: 'Low',
      });
    }

    // Scores by category
    const scores: Record<string, number> = {
      'gpl-agpl': analysis.gplPackages.length === 0 ? 100 : 0,
      'commercial': analysis.commercialIssues.length === 0 ? 100 : 0,
      'copyleft': analysis.copyleftPackages.length === 0 ? 100 : 50,
      'attribution': 100, // Attribution is manageable
    };

    const assessment = await saveResult({
      name: `License Compliance - ${new Date().toLocaleDateString()}`,
      score_overall: overallScore,
      scores,
      answers: {
        isCommercialProduct,
        packages: packages.map(p => ({ name: p.name, version: p.version, license: p.license })),
      },
      gaps,
      recommendations,
      metadata: {
        totalPackages: analysis.totalPackages,
        complianceStatus: analysis.complianceStatus,
        gplPackageCount: analysis.gplPackages.length,
        commercialIssueCount: analysis.commercialIssues.length,
        copyleftPackageCount: analysis.copyleftPackages.length,
        attributionRequiredCount: analysis.requiresAttribution.length,
        sourceDisclosureRequiredCount: analysis.requiresSourceDisclosure.length,
        licenseCounts: analysis.licenseCounts,
        riskCounts: analysis.riskCounts,
      },
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const getComplianceColor = () => {
    switch (analysis.complianceStatus) {
      case 'compliant': return 'text-green-500';
      case 'warning': return 'text-yellow-500';
      case 'critical': return 'text-red-500';
    }
  };

  const getComplianceBgColor = () => {
    switch (analysis.complianceStatus) {
      case 'compliant': return 'bg-green-500/10 border-green-500/30';
      case 'warning': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'critical': return 'bg-red-500/10 border-red-500/30';
    }
  };

  const getRiskColor = (risk: RiskLevel) => {
    switch (risk) {
      case 'low': return 'text-green-500';
      case 'medium': return 'text-yellow-500';
      case 'high': return 'text-accent';
      case 'critical': return 'text-red-500';
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link href="/tools" className="text-accent hover:text-accent mb-4 inline-block">
            ← Back to Tools
          </Link>
          <h1 className="text-4xl font-bold mb-4 text-light-text dark:text-foreground">
            Open Source License Compliance Checker
          </h1>
          <p className="text-xl text-light-text-secondary dark:text-foreground-secondary">
            Scan dependencies for license compliance, detect GPL/AGPL conflicts, check commercial use compatibility,
            and generate compliance reports for legal review.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column: Configuration */}
          <div className="space-y-6">
            {/* Project Context */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Project Context
              </h2>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isCommercialProduct}
                  onChange={(e) => setIsCommercialProduct(e.target.checked)}
                  className="w-5 h-5"
                />
                <span className="text-light-text dark:text-foreground">This is a commercial product</span>
              </label>

              <p className="text-xs text-light-text-secondary dark:text-foreground-secondary mt-3">
                Affects compatibility analysis for licenses with commercial restrictions
              </p>
            </div>

            {/* Packages */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-light-text dark:text-foreground">
                  Packages ({analysis.validPackages.length})
                </h2>
                <button
                  onClick={addPackage}
                  className="px-4 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors text-sm"
                >
                  + Add Package
                </button>
              </div>

              <div className="space-y-3 max-h-[500px] overflow-y-auto">
                {packages.map((pkg) => (
                  <div key={pkg.id} className="border border-gray-300 dark:border-border rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Package name"
                          value={pkg.name}
                          onChange={(e) => updatePackage(pkg.id, 'name', e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Version"
                          value={pkg.version}
                          onChange={(e) => updatePackage(pkg.id, 'version', e.target.value)}
                          className="px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                        />
                      </div>
                      <button
                        onClick={() => removePackage(pkg.id)}
                        className="ml-2 text-red-500 hover:text-red-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div>
                      <label className="block text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                        License
                      </label>
                      <select
                        value={pkg.license}
                        onChange={(e) => updatePackage(pkg.id, 'license', e.target.value as LicenseType)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-border rounded-lg bg-white dark:bg-background text-light-text dark:text-foreground text-sm"
                      >
                        <option value="MIT">MIT</option>
                        <option value="Apache-2.0">Apache 2.0</option>
                        <option value="BSD-3">BSD 3-Clause</option>
                        <option value="ISC">ISC</option>
                        <option value="Unlicense">Unlicense</option>
                        <option value="MPL">MPL</option>
                        <option value="LGPL">LGPL</option>
                        <option value="GPL-2.0">GPL 2.0</option>
                        <option value="GPL-3.0">GPL 3.0</option>
                        <option value="AGPL">AGPL</option>
                        <option value="Proprietary">Proprietary</option>
                        <option value="Unknown">Unknown</option>
                      </select>
                    </div>

                    {/* Risk indicator */}
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-light-text-secondary dark:text-foreground-tertiary">Risk:</span>
                      <span className={`text-xs font-semibold ${getRiskColor(LICENSE_DATABASE[pkg.license].riskLevel)}`}>
                        {LICENSE_DATABASE[pkg.license].riskLevel.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {packages.length >= 20 && (
                <div className="mt-3 text-sm text-accent">
                  ⚠️ Free tier limited to 20 packages. Upgrade to Pro for file upload scanning.
                </div>
              )}
            </div>
          </div>

          {/* Right Column: Analysis */}
          <div className="space-y-6">
            {/* Compliance Status */}
            <div className={`border rounded-lg p-6 ${getComplianceBgColor()}`}>
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Compliance Status
              </h2>

              <div className="text-center mb-6">
                <div className={`text-4xl font-bold mb-2 ${getComplianceColor()}`}>
                  {analysis.complianceStatus === 'compliant' ? '✓ COMPLIANT' :
                   analysis.complianceStatus === 'warning' ? '⚠ WARNING' : '✗ CRITICAL ISSUES'}
                </div>
                <div className="text-light-text-secondary dark:text-foreground-secondary">
                  {analysis.totalPackages} packages scanned
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Critical Risk
                  </div>
                  <div className="text-2xl font-bold text-red-500">
                    {analysis.riskCounts.critical || 0}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    High Risk
                  </div>
                  <div className="text-2xl font-bold text-accent">
                    {analysis.riskCounts.high || 0}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    GPL/AGPL
                  </div>
                  <div className="text-2xl font-bold text-red-500">
                    {analysis.gplPackages.length}
                  </div>
                </div>

                <div className="bg-white dark:bg-surface rounded-lg p-4">
                  <div className="text-xs text-light-text-secondary dark:text-foreground-tertiary mb-1">
                    Copyleft
                  </div>
                  <div className="text-2xl font-bold text-accent">
                    {analysis.copyleftPackages.length}
                  </div>
                </div>
              </div>
            </div>

            {/* Issues */}
            {(analysis.gplPackages.length > 0 || analysis.commercialIssues.length > 0) && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                <h2 className="text-2xl font-bold mb-4 text-red-500">
                  Critical Issues
                </h2>

                <div className="space-y-3">
                  {analysis.gplPackages.length > 0 && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
                      <div className="font-semibold text-red-500 mb-2">
                        GPL/AGPL Detected ({analysis.gplPackages.length} packages)
                      </div>
                      <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
                        These licenses require your entire codebase to be open-sourced under the same license.
                      </p>
                      <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-1">
                        {analysis.gplPackages.map(pkg => (
                          <li key={pkg.id}>• {pkg.name} ({pkg.license})</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {analysis.commercialIssues.length > 0 && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-3">
                      <div className="font-semibold text-red-500 mb-2">
                        Commercial Use Issues ({analysis.commercialIssues.length} packages)
                      </div>
                      <p className="text-sm text-light-text-secondary dark:text-foreground-secondary mb-2">
                        These packages have restrictions or unknown status for commercial use.
                      </p>
                      <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-1">
                        {analysis.commercialIssues.map(pkg => (
                          <li key={pkg.id}>• {pkg.name} ({pkg.license})</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Obligations */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                Legal Obligations
              </h2>

              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-300 dark:border-border">
                  <span className="text-sm text-light-text dark:text-foreground">Attribution Required</span>
                  <span className="text-sm font-bold text-accent">
                    {analysis.requiresAttribution.length} packages
                  </span>
                </div>

                <div className="flex justify-between items-center py-2 border-b border-gray-300 dark:border-border">
                  <span className="text-sm text-light-text dark:text-foreground">Source Disclosure Required</span>
                  <span className="text-sm font-bold text-accent">
                    {analysis.requiresSourceDisclosure.length} packages
                  </span>
                </div>

                <div className="flex justify-between items-center py-2">
                  <span className="text-sm text-light-text dark:text-foreground">Copyleft Packages</span>
                  <span className="text-sm font-bold text-accent">
                    {analysis.copyleftPackages.length} packages
                  </span>
                </div>
              </div>

              <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-xs text-light-text-secondary dark:text-foreground-secondary">
                  <span className="font-semibold text-light-text dark:text-foreground">Tip:</span>{' '}
                  Include a LICENSES.txt file in your distribution with all required attributions.
                </p>
              </div>
            </div>

            {/* License Distribution */}
            <div className="bg-white dark:bg-surface border border-gray-300 dark:border-border rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4 text-light-text dark:text-foreground">
                License Distribution
              </h2>

              <div className="space-y-2">
                {Object.entries(analysis.licenseCounts)
                  .sort(([, a], [, b]) => b - a)
                  .map(([license, count]) => {
                    const licenseInfo = LICENSE_DATABASE[license as LicenseType];
                    return (
                      <div key={license} className="flex items-center gap-3">
                        <span className="text-sm text-light-text-secondary dark:text-foreground-tertiary w-32 truncate">
                          {licenseInfo.name}
                        </span>
                        <div className="flex-1 bg-gray-200 dark:bg-background rounded-full h-6 overflow-hidden">
                          <div
                            className={`h-full flex items-center justify-end px-2 ${
                              licenseInfo.riskLevel === 'critical' ? 'bg-red-500' :
                              licenseInfo.riskLevel === 'high' ? 'bg-accent' :
                              licenseInfo.riskLevel === 'medium' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${(count / analysis.totalPackages) * 100}%` }}
                          >
                            <span className="text-xs font-semibold text-white">{count}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>

            {/* Export and Save */}
            <div className="space-y-3">
              <button
                onClick={exportToExcel}
                className="w-full px-6 py-3 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Export Compliance Report to Excel
              </button>
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="w-full px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-semibold flex items-center justify-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved to Command Center
                </div>
              )}
              {saveError && (
                <div className="w-full px-6 py-3 bg-red-600/20 text-red-400 rounded-lg text-sm text-center">
                  {saveError}
                </div>
              )}
              <button
                onClick={() => {
                  setPackages([
                    { id: '1', name: 'react', version: '18.2.0', license: 'MIT' },
                    { id: '2', name: 'express', version: '4.18.2', license: 'MIT' },
                  ]);
                  setIsCommercialProduct(true);
                  setSaved(false);
                }}
                className="w-full px-6 py-3 border border-gray-300 dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                Start Over
              </button>
            </div>
          </div>
        </div>

        {/* Pro Features */}
        <div className="mt-8 bg-gradient-to-r from-accent-muted to-accent/5 border-l-4 border-accent p-6 rounded-lg">
          <h3 className="text-xl font-bold text-accent mb-3">⭐ Unlock Pro Features</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Pro ($29/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ File upload scanning (unlimited packages)</li>
                <li>✓ GPL/AGPL conflict detection</li>
                <li>✓ Commercial use analyzer</li>
                <li>✓ Excel export with obligations</li>
                <li>✓ License risk scoring</li>
              </ul>
            </div>
            <div>
              <div className="font-semibold text-light-text dark:text-foreground mb-2">Team ($99/month):</div>
              <ul className="space-y-1 text-sm text-light-text-secondary dark:text-foreground-secondary">
                <li>✓ Everything in Pro</li>
                <li>✓ SBOM (Software Bill of Materials) generator</li>
                <li>✓ Vulnerability database integration</li>
                <li>✓ Auto-remediation suggestions</li>
                <li>✓ Legal-ready compliance reports</li>
              </ul>
            </div>
          </div>
          <Link
            href="/pricing"
            className="inline-block px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
          >
            Upgrade to Pro →
          </Link>
        </div>
      </div>
    </div>
  );
}
